import React, { useState, useEffect } from 'react';
import {
  Clock,
  CheckCircle,
  XCircle,
  X,
  RefreshCw,
  Loader2,
  AlertCircle,
  User,
  ChevronDown,
  ChevronUp,
  Eye,
  ClipboardCheck,
} from 'lucide-react';
import {
  getPendingChangeRequests,
  type ChangeRequest,
  FIELD_LABELS,
  type ChangeableField,
  type UserTypeFilter,
} from '@/services/changeRequestService';
import { ChangeRequestReviewModal } from './ChangeRequestReviewModal';

interface AdminChangeRequestsCardProps {
  className?: string;
  userTypeFilter?: 'staff' | 'student' | 'all';
  onRequestReviewed?: () => void; // Called when a request is approved/rejected to refresh parent stats
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'PENDING':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <Clock className="w-3 h-3" />
          En attente
        </span>
      );
    case 'APPROVED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Approuvée
        </span>
      );
    case 'REJECTED':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle className="w-3 h-3" />
          Refusée
        </span>
      );
    default:
      return null;
  }
};

const getRoleName = (role: string): string => {
  const roleNames: Record<string, string> = {
    STUDENT: 'Étudiant',
    PARENT: 'Parent',
    TEACHER: 'Professeur',
    DIRECTOR_GENERAL: 'Directeur Général',
    PEDAGOGICAL_DIRECTOR: 'Directeur Pédagogique',
    BRANCH_MANAGER: 'Responsable de Site',
    PROGRAM_HEAD: 'Chef de Filière',
    ACADEMIC_COORDINATOR: 'Coordinateur Académique',
    FINANCIAL_MANAGER: 'Responsable Financier',
    MARKETING_MANAGER: 'Responsable Marketing',
    COMMERCIAL_MANAGER: 'Responsable Commercial',
    SUPER_ADMIN: 'Super Administrateur',
  };
  return roleNames[role] || role;
};

export const AdminChangeRequestsCard: React.FC<AdminChangeRequestsCardProps> = ({
  className = '',
  userTypeFilter = 'all',
  onRequestReviewed,
}) => {
  const [requests, setRequests] = useState<ChangeRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<ChangeRequest | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const loadRequests = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Pass userTypeFilter to the API - backend will filter based on permissions
      const apiUserType: UserTypeFilter | undefined = userTypeFilter === 'all' ? undefined : userTypeFilter;
      const data = await getPendingChangeRequests(apiUserType);
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [userTypeFilter]);

  const handleOpenReview = (request: ChangeRequest) => {
    setSelectedRequest(request);
    setShowReviewModal(true);
  };

  const handleReviewComplete = () => {
    loadRequests();
    // Notify parent to refresh its stats (pending counts, etc.)
    onRequestReviewed?.();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getFieldLabel = (fieldName: string): string => {
    return FIELD_LABELS[fieldName as ChangeableField] || fieldName;
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <ClipboardCheck className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-gray-900">
              Demandes de modification
            </h3>
            <p className="text-sm text-gray-500">
              {requests.length} demande{requests.length > 1 ? 's' : ''} en attente
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              loadRequests();
            }}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Actualiser"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </button>

      {/* Content */}
      {expanded && (
        <div className="px-6 pb-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
            </div>
          ) : requests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 text-green-300 mb-3" />
              <p className="text-sm font-medium">Aucune demande en attente</p>
              <p className="text-xs text-gray-400 mt-1">Toutes les demandes ont été traitées</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  {/* User info */}
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {request.user?.firstName} {request.user?.lastName}
                        </p>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <span>{request.user?.email}</span>
                          <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">
                            {request.user?.role && getRoleName(request.user.role)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <StatusBadge status={request.status} />
                  </div>

                  {/* Change request details */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200 mb-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Champ: <span className="text-gray-900">{getFieldLabel(request.fieldName)}</span>
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Valeur actuelle:</span>
                        <p className="text-gray-700 font-mono bg-gray-100 px-2 py-1 rounded mt-1 truncate">
                          {request.currentValue || '(non renseigné)'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Nouvelle valeur:</span>
                        <p className="text-gray-900 font-medium font-mono bg-green-50 px-2 py-1 rounded mt-1 truncate">
                          {request.requestedValue}
                        </p>
                      </div>
                    </div>
                    {request.reason && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-sm text-gray-500">Raison:</span>
                        <p className="text-sm text-gray-700 mt-1">{request.reason}</p>
                      </div>
                    )}
                  </div>

                  {/* Date */}
                  <p className="text-xs text-gray-500 mb-3">
                    Soumise le {formatDate(request.createdAt)}
                  </p>

                  {/* Action button */}
                  <button
                    onClick={() => handleOpenReview(request)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    <Eye className="w-4 h-4" />
                    Examiner la demande
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Review Modal */}
      <ChangeRequestReviewModal
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedRequest(null);
        }}
        request={selectedRequest}
        onReviewComplete={handleReviewComplete}
      />
    </div>
  );
};

export default AdminChangeRequestsCard;
